import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spinner, Button, Alert } from "react-bootstrap";

export default function CustomerGallery({
  apiBase = "/api",
  customerId,
  customerName,
  onAttach,
}) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [attachingId, setAttachingId] = useState(null);

  // Fetch images
  useEffect(() => {
    if (customerId) {
      fetchImages();
    } else {
      setImages([]);
    }
  }, [customerId]);

  const fetchImages = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${apiBase}/customers/${customerId}/images/`);
      const data = await res.json();
      if (!res.ok) throw new Error("Failed to load images");

      setImages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;

    setDeletingId(id);
    try {
      const res = await fetch(`${apiBase}/images/${id}/delete/`, {
        method: "DELETE",
      });

      if (!res.ok) throw new Error("Delete failed");

      // Remove from UI
      setImages((prev) => prev.filter((img) => img.id !== id));
    } catch (err) {
      alert(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const sanitizeForFilename = (value) =>
    String(value || "")
      .trim()
      .replace(/[\\/:*?"<>|]+/g, "");

  const buildFileName = (img) => {
    const customer = sanitizeForFilename(customerName) || "Customer";
    const jewelleryType = sanitizeForFilename(img.jewellery_type) || "Design";
    const sessionLabel = sanitizeForFilename(
      img.session_title || img.session_id,
    );

    return `${customer} ${jewelleryType} S${sessionLabel}.png`;
  };

  const handleSave = async (img) => {
    setSavingId(img.id);
    try {
      const res = await fetch(img.url);
      if (!res.ok) throw new Error("Failed to download image");
      const blob = await res.blob();

      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = buildFileName(img);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      alert(err.message || "Failed to save image");
    } finally {
      setSavingId(null);
    }
  };

  const handleAttach = async (img) => {
    if (!onAttach) return;

    setAttachingId(img.id);
    try {
      await onAttach(img);
    } catch (err) {
      alert(err.message || "Failed to attach image");
    } finally {
      setAttachingId(null);
    }
  };

  const heading = customerName ? `${customerName}'s Gallery` : "Gallery";

  return (
    <Card className="shadow-sm mt-3">
      <Card.Body>
        <h4 className="fw-semibold mb-3" style={{ color: "#000" }}>
          {heading}
        </h4>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <Spinner animation="border" />
        ) : images.length === 0 ? (
          <div className="text-muted">No images found for this customer</div>
        ) : (
          <Row className="g-3">
            {images.map((img) => (
              <Col xs={6} md={4} lg={3} key={img.id}>
                <Card className="h-100">
                  <a href={img.url} target="_blank" rel="noreferrer">
                    <Card.Img
                      src={img.url}
                      style={{
                        height: 180,
                        objectFit: "cover",
                      }}
                    />
                  </a>

                  <Card.Body className="p-2 d-grid gap-1">
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => handleSave(img)}
                      disabled={savingId === img.id}
                    >
                      {savingId === img.id ? "Saving..." : "Save"}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-primary"
                      onClick={() => handleAttach(img)}
                      disabled={attachingId === img.id}
                    >
                      {attachingId === img.id ? "Attaching..." : "Attach"}
                    </Button>

                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(img.id)}
                      disabled={deletingId === img.id}
                    >
                      {deletingId === img.id ? "Deleting..." : "Delete"}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Card.Body>
    </Card>
  );
}
